import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ButtonBase,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Tooltip,
  Alert,
  Divider,
  Paper,
  Popper,
  Stack,
  Tabs,
  Tab,
  Snackbar,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { createTicket } from '../../store/slices/questsSlice';
import { getMasters, fetchServices } from '../../store/slices/questsSlice';
import { ticketService } from '../../services/questService';
import { appealService } from '../../services/appealService';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import EngineeringIcon from '@mui/icons-material/Engineering';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SearchIcon from '@mui/icons-material/Search';

import { useDropzone } from 'react-dropzone';
import {
  DEFAULT_DATETIME_LOCAL_TIME,
  combineDateAndTimeToIso,
  getLocalDateInputValue,
  isDeadlineBeforeNow,
  toDateInputValue,
  toTimeInputValue,
} from '../../utils/datetimeLocal';
import { MAX_ATTACHMENT_BYTES, tooLargeAttachmentMessage } from '../../utils/attachmentSize';

const getMasterName = (m) =>
  [m.lastName, m.firstName, m.patronymic].filter(Boolean).join(' ') || m.name || m.login || '—';

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Б';
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
};

const formatCurrency = (value) => new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
}).format(value);

const clientNameFromAccount = (prev, account) =>
  (prev.relatedTicketId && String(prev.clientName || '').trim())
    ? prev.clientName
    : (account?.responsiblePerson ?? account?.ResponsiblePerson ?? prev.clientName);

const REQUIRED_FIELDS = ['masterId', 'serviceId', 'title', 'deadlineDate', 'clientName', 'address'];
const DEADLINE_PAST_ERROR = 'Срок выполнения не может быть раньше даты создания заявки';
const CLIENT_PHONE_PREFIX = '+7';

const normalizeClientPhone = (value) => {
  let digits = String(value ?? '').replace(/\D/g, '');
  if (digits.startsWith('7') || (digits.startsWith('8') && digits.length > 10)) {
    digits = digits.slice(1);
  }
  return `${CLIENT_PHONE_PREFIX}${digits.slice(0, 10)}`;
};

const CreateQuestForm = ({ open, onClose, onQuestCreated, initialData }) => {
  const dispatch = useDispatch();

  const { masters, services, loading, error } = useSelector((state) => ({
    masters: state.quests.masters,
    services: state.quests.services,
    loading: state.quests.mastersLoading || state.quests.servicesLoading,
    error: state.quests.servicesError || state.quests.error,
  }));

  const defaultFormData = {
    masterId: '',
    serviceId: '',
    title: '',
    description: '',
    deadlineDate: '',
    deadlineTime: DEFAULT_DATETIME_LOCAL_TIME,
    address: '',
    phoneNumber: CLIENT_PHONE_PREFIX,
    clientName: '',
    accountNumber: '',
    clientComment: '',
    fixedPrice: '',
    attachmentFileIds: [],
    relatedTicketId: null,
  };

  const [formData, setFormData] = useState({
    ...defaultFormData,
    ...initialData,
    phoneNumber: normalizeClientPhone(initialData?.phoneNumber),
  });

  const [files, setFiles] = useState([]);
  const [fileSizeWarning, setFileSizeWarning] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [touched, setTouched] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [accountSearchResults, setAccountSearchResults] = useState([]);
  const [accountSearchLoading, setAccountSearchLoading] = useState(false);
  const [accountSearchError, setAccountSearchError] = useState('');
  const [showAccountSearchHint, setShowAccountSearchHint] = useState(true);
  const [accountBalance, setAccountBalance] = useState(null);
  const [accountBalanceLoading, setAccountBalanceLoading] = useState(false);
  const [accountBalanceError, setAccountBalanceError] = useState('');
  const accountFieldRef = useRef(null);
  const fileSizeWarningRef = useRef(null);

  useEffect(() => {
    if (!fileSizeWarning) return;
    fileSizeWarningRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [fileSizeWarning]);

  useEffect(() => {
    if (open) {
      dispatch(getMasters());
      dispatch(fetchServices());
      if (initialData) {
        setFormData(prev => ({
          ...prev,
          ...initialData,
          phoneNumber: normalizeClientPhone(initialData.phoneNumber ?? prev.phoneNumber),
          deadlineDate: toDateInputValue(initialData.deadline) || prev.deadlineDate,
          deadlineTime: toTimeInputValue(initialData.deadline) || prev.deadlineTime,
        }));
      }
    }
  }, [open, dispatch, initialData]);

  // Загрузка данных заявки по ID для заполнения исполнителя
  useEffect(() => {
    const loadQuestData = async () => {
      // Если initialData содержит id заявки и нет masterId, загружаем заявку
      if (open && initialData?.id && !initialData.masterId) {
        try {
          const quest = await ticketService.getTicket(initialData.id);
          if (quest?.masterId) {
            setFormData(prev => ({ ...prev, masterId: quest.masterId }));
          }
        } catch (err) {
          console.error('Ошибка загрузки заявки:', err);
        }
      }
    };
    loadQuestData();
  }, [open, initialData]);

  const resetAccountBalance = () => {
    setAccountBalance(null);
    setAccountBalanceError('');
    setAccountBalanceLoading(false);
  };

  const loadAccountBalance = async (account) => {
    const accountId = account?.id ?? account?.Id;
    setAccountBalance(null);
    setAccountBalanceError('');

    if (!accountId) {
      setAccountBalanceError('Не удалось получить идентификатор лицевого счёта');
      return;
    }

    setAccountBalanceLoading(true);
    try {
      const balance = await appealService.getAccountBalance(accountId);
      setAccountBalance(balance);
    } catch (err) {
      setAccountBalanceError('Не удалось загрузить баланс лицевого счёта');
    } finally {
      setAccountBalanceLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name } = e.target;
    const value = name === 'phoneNumber' ? normalizeClientPhone(e.target.value) : e.target.value;
    if (name === 'accountNumber') {
      setAccountSearchResults([]);
      setAccountSearchError('');
      setShowAccountSearchHint(true);
      resetAccountBalance();
    }

    setFormData(prev => {
      const nextFormData = { ...prev, [name]: value };

      if (name === 'serviceId') {
        const selectedService = services?.find(service => service.id === value);
        const previousService = services?.find(service => service.id === prev.serviceId);
        const currentTitle = prev.title.trim();
        const titleFollowsService = !currentTitle || currentTitle === previousService?.name?.trim();
        if (selectedService?.name && titleFollowsService) {
          nextFormData.title = selectedService.name;
        }
        if (selectedService?.price != null && selectedService.price !== '') {
          nextFormData.fixedPrice = String(selectedService.price);
        }
      }

      return nextFormData;
    });
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const applyAccountSearchResult = (account) => {
    setFormData(prev => ({
      ...prev,
      accountNumber: account?.number || '',
      clientName: clientNameFromAccount(prev, account),
      address: account?.address || prev.address,
    }));
    setAccountSearchResults([]);
    setAccountSearchError('');
    loadAccountBalance(account);
  };

  const clearAccountSearchResults = () => {
    setFormData(prev => ({
      ...prev,
      accountNumber: '',
      clientName: '',
      address: '',
      relatedTicketId: null,
    }));
    setAccountSearchResults([]);
    setAccountSearchError('');
    setShowAccountSearchHint(true);
    resetAccountBalance();
  };

  const handleAccountSearch = async () => {
    const number = formData.accountNumber.trim();
    setAccountSearchResults([]);
    setShowAccountSearchHint(false);
    resetAccountBalance();

    if (!number) {
      setAccountSearchError('Введите номер лицевого счёта для поиска');
      return;
    }

    setAccountSearchLoading(true);
    setAccountSearchError('');

    try {
      const accounts = await appealService.searchAccountsByNumber(number);
      if (!Array.isArray(accounts) || accounts.length === 0) {
        setAccountSearchError('Лицевой счёт не найден');
        return;
      }

      if (accounts.length === 1) {
        applyAccountSearchResult(accounts[0]);
        return;
      }

      setAccountSearchResults(accounts);
    } catch (err) {
      setAccountSearchError('Ошибка поиска лицевого счёта');
    } finally {
      setAccountSearchLoading(false);
    }
  };

  useEffect(() => {
    const number = initialData?.accountNumber?.trim();
    if (!open || !number) return undefined;

    let cancelled = false;

    const fillAccountFromInitialData = async () => {
      setAccountSearchResults([]);
      setAccountSearchError('');
      setShowAccountSearchHint(false);
      setAccountBalance(null);
      setAccountBalanceError('');
      setAccountBalanceLoading(false);
      setAccountSearchLoading(true);

      try {
        const accounts = await appealService.searchAccountsByNumber(number);
        if (cancelled) return;

        if (!Array.isArray(accounts) || accounts.length === 0) {
          setAccountSearchError('Лицевой счёт не найден');
          return;
        }

        const account = accounts.find(item => String(item?.number ?? item?.Number) === number) || accounts[0];
        const accountNumber = account?.number ?? account?.Number ?? number;

        setFormData(prev => ({
          ...prev,
          accountNumber,
          clientName: clientNameFromAccount(prev, account),
          address: account?.address ?? account?.Address ?? prev.address,
        }));

        const accountId = account?.id ?? account?.Id;
        if (!accountId) {
          setAccountBalanceError('Не удалось получить идентификатор лицевого счёта');
          return;
        }

        setAccountBalanceLoading(true);
        try {
          const balance = await appealService.getAccountBalance(accountId);
          if (!cancelled) {
            setAccountBalance(balance);
          }
        } catch (err) {
          if (!cancelled) {
            setAccountBalanceError('Не удалось загрузить баланс лицевого счёта');
          }
        } finally {
          if (!cancelled) {
            setAccountBalanceLoading(false);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setAccountSearchError('Ошибка поиска лицевого счёта');
        }
      } finally {
        if (!cancelled) {
          setAccountSearchLoading(false);
        }
      }
    };

    fillAccountFromInitialData();

    return () => {
      cancelled = true;
    };
  }, [open, initialData?.accountNumber]);

  const deadlineIsPast = () => isDeadlineBeforeNow(formData.deadlineDate, formData.deadlineTime);

  const getFieldError = (name) => {
    if (!touched[name]) return '';
    if (name === 'deadlineDate') {
      if (!formData.deadlineDate?.trim()) return 'Обязательное поле';
      if (deadlineIsPast()) return DEADLINE_PAST_ERROR;
      return '';
    }
    if (!REQUIRED_FIELDS.includes(name)) return '';
    return formData[name]?.trim() ? '' : 'Обязательное поле';
  };

  const hasErrors = () => REQUIRED_FIELDS.some(f => !formData[f]?.trim()) || deadlineIsPast();
  const hasTabErrors = (tab) => {
    const tabFields = [
      ['clientName', 'address'],
      ['masterId', 'title', 'deadlineDate'],
      [],
    ];
    const missing = tabFields[tab]?.some(f => touched[f] && !formData[f]?.trim());
    const pastDeadline = tab === 1 && touched.deadlineDate && deadlineIsPast();
    return missing || pastDeadline;
  };

  const handleFileDrop = (acceptedFiles, fileRejections) => {
    if (acceptedFiles?.length) {
      setFiles(prev => [...prev, ...acceptedFiles]);
    }
    setFileSizeWarning(tooLargeAttachmentMessage(fileRejections));
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    const allTouched = {};
    REQUIRED_FIELDS.forEach(f => { allTouched[f] = true; });
    setTouched(allTouched);

    if (deadlineIsPast()) {
      setActiveTab(1);
      setSubmitError(DEADLINE_PAST_ERROR);
      return;
    }

    if (hasErrors()) return;

    setIsSubmitting(true);

    try {
      const attachmentIds = [];
      for (const file of files) {
        const uploadResult = await ticketService.uploadNewQuestAttachment(file);
        attachmentIds.push(uploadResult.attachmentId ?? uploadResult.fileId);
      }

      const { deadlineDate, deadlineTime, ...formRest } = formData;
      const questData = {
        ...formRest,
        phoneNumber: formData.phoneNumber === CLIENT_PHONE_PREFIX ? '' : formData.phoneNumber,
        fixedPrice: parseFloat(formData.fixedPrice) || 0,
        deadline: combineDateAndTimeToIso(deadlineDate, deadlineTime),
        attachmentFileIds: attachmentIds,
      };

      console.log('=== CREATE QUEST REQUEST ===');
      console.log(JSON.stringify(questData, null, 2));

      const result = await dispatch(createTicket(questData)).unwrap();

      if (onQuestCreated) {
        onQuestCreated(result);
      }

      resetForm();
      onClose();
    } catch (err) {
      setSubmitError(typeof err === 'string' ? err : 'Ошибка создания заявки. Проверьте заполнение полей.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ ...defaultFormData });
    setFiles([]);
    setFileSizeWarning('');
    setTouched({});
    setActiveTab(0);
    setSubmitError(null);
    setAccountSearchResults([]);
    setAccountSearchError('');
    setShowAccountSearchHint(true);
    setAccountSearchLoading(false);
    resetAccountBalance();
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileDrop,
    maxSize: MAX_ATTACHMENT_BYTES,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.bmp'],
      'application/*': ['.pdf', '.doc', '.docx', '.txt'],
    },
    multiple: true,
  });

  const showAccountClearButton = Boolean(
    formData.accountNumber || accountSearchResults.length > 0 || accountSearchError
  );
  const accountBalanceValue = Number(accountBalance?.balance ?? accountBalance?.Balance);
  const hasAccountBalanceValue = Number.isFinite(accountBalanceValue);
  const accountBalanceText = hasAccountBalanceValue ? formatCurrency(accountBalanceValue) : '';
  const accountBalanceColor = accountBalanceValue < 0
    ? 'success.main'
    : accountBalanceValue > 0
      ? 'error.main'
      : 'text.primary';
  const accountBalanceFieldValue = accountBalanceLoading
    ? 'Загрузка...'
    : accountBalanceError || accountBalanceText;
  const showAccountBalance = accountBalanceLoading || accountBalanceError || accountBalanceText;
  const dialogTitle = formData.relatedTicketId ? 'Создание заявки из обращения' : 'Создание заявки';
  const minDeadlineDate = getLocalDateInputValue();

  const tabLabels = [
    { label: 'Клиент', icon: <PersonIcon sx={{ fontSize: 18 }} /> },
    { label: 'Основная информация', icon: <WorkIcon sx={{ fontSize: 18 }} /> },
    { label: 'Суть работы и вложения', icon: <DescriptionIcon sx={{ fontSize: 18 }} /> },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          overflow: 'hidden',
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {dialogTitle}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: '1.6 1 0', minWidth: 0 }}>
            <Box>
              <Box ref={accountFieldRef} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ position: 'relative', flex: 1, minWidth: 0 }}>
                  <TextField
                    fullWidth
                    name="accountNumber"
                    label="Номер лицевого счёта"
                    value={formData.accountNumber}
                    onChange={handleInputChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAccountSearch();
                      }
                    }}
                    placeholder="Введите номер или часть номера"
                    error={!!accountSearchError}
                    helperText={accountSearchError || (showAccountSearchHint ? 'Введите часть номера и нажмите «Найти»' : '')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        pr: showAccountClearButton ? 5 : undefined,
                      },
                    }}
                  />
                  {showAccountClearButton && (
                    <Tooltip title="Очистить данные клиента">
                      <IconButton
                        size="small"
                        onClick={clearAccountSearchResults}
                        aria-label="Очистить номер лицевого счёта и данные клиента"
                        sx={{
                          position: 'absolute',
                          top: 11,
                          right: 8,
                          zIndex: 1,
                          color: 'text.secondary',
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Button
                  variant="contained"
                  onClick={handleAccountSearch}
                  disabled={accountSearchLoading}
                  startIcon={accountSearchLoading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon fontSize="small" />}
                  sx={{ height: 56, minWidth: 118, textTransform: 'none', fontWeight: 600 }}
                >
                  Найти
                </Button>
              </Box>
              <Popper
                open={accountSearchResults.length > 1}
                anchorEl={accountFieldRef.current}
                placement="bottom-start"
                sx={{ zIndex: 1300, width: accountFieldRef.current?.offsetWidth || 'auto' }}
              >
                <Paper variant="outlined" sx={{ mt: 0, maxHeight: 240, overflow: 'auto' }}>
                  {accountSearchResults.map(account => (
                    <ButtonBase
                      key={account.id}
                      onClick={() => applyAccountSearchResult(account)}
                      sx={{
                        display: 'block',
                        width: '100%',
                        px: 2,
                        py: 1,
                        textAlign: 'left',
                        whiteSpace: 'normal',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      {account.number} - {account.responsiblePerson || 'Без ответственного'}
                    </ButtonBase>
                  ))}
                </Paper>
              </Popper>
            </Box>
          </Box>

          {showAccountBalance && (
            <TextField
              fullWidth
              label="Баланс лицевого счёта"
              value={accountBalanceFieldValue}
              error={!!accountBalanceError}
              InputProps={{ readOnly: true }}
              sx={{
                flex: '1 1 0',
                minWidth: 220,
                '& .MuiOutlinedInput-root': {
                  bgcolor: accountBalanceError ? 'rgba(211, 47, 47, 0.06)' : 'rgba(0, 136, 105, 0.06)',
                  '& fieldset': {
                    borderColor: accountBalanceError ? 'error.main' : 'primary.main',
                  },
                  '&:hover fieldset': {
                    borderColor: accountBalanceError ? 'error.main' : 'primary.main',
                  },
                },
                '& .MuiInputBase-input': {
                  textAlign: 'right',
                  color: accountBalanceError ? 'error.main' : accountBalanceColor,
                },
              }}
            />
          )}
        </Box>

        <FormControl fullWidth required error={!!getFieldError('serviceId')}>
          <InputLabel>Вид заявки</InputLabel>
          <Select
            name="serviceId"
            value={formData.serviceId}
            onChange={handleInputChange}
            onBlur={handleBlur}
            label="Вид заявки"
          >
            <MenuItem value=""><em style={{ color: '#999' }}>Не выбран</em></MenuItem>
            {services?.map(service => (
              <MenuItem key={service.id} value={service.id}>{service.name}</MenuItem>
            ))}
          </Select>
          {getFieldError('serviceId') && <FormHelperText>{getFieldError('serviceId')}</FormHelperText>}
        </FormControl>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="fullWidth"
          sx={{ minHeight: 44 }}
        >
          {tabLabels.map((tab, i) => (
            <Tab
              key={i}
              icon={tab.icon}
              iconPosition="start"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {tab.label}
                  {hasTabErrors(i) && (
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        bgcolor: 'error.main',
                        ml: 0.5,
                      }}
                    />
                  )}
                </Box>
              }
              sx={{
                minHeight: 44,
                textTransform: 'none',
                fontSize: '0.85rem',
              }}
            />
          ))}
        </Tabs>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5, minHeight: 0 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={36} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && (
          <Box component="form" noValidate autoComplete="off">
            {/* Tab 1: Основная информация */}
            {activeTab === 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth required error={!!getFieldError('masterId')}>
                    <InputLabel>Исполнитель</InputLabel>
                    <Select
                      name="masterId"
                      value={formData.masterId}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      label="Исполнитель"
                      startAdornment={<EngineeringIcon sx={{ mr: 1.5, color: 'text.secondary', fontSize: 20 }} />}
                      sx={{ '& .MuiSelect-select': { display: 'flex', alignItems: 'center' } }}
                    >
                      <MenuItem value=""><em style={{ color: '#999' }}>Не выбран</em></MenuItem>
                      {masters?.map(master => (
                        <MenuItem key={master.id} value={master.id}>{getMasterName(master)}</MenuItem>
                      ))}
                    </Select>
                    {getFieldError('masterId') && <FormHelperText>{getFieldError('masterId')}</FormHelperText>}
                  </FormControl>
                </Box>

                <TextField
                  fullWidth
                  name="title"
                  label="Название заявки"
                  value={formData.title}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  placeholder="Введите краткое название"
                  required
                  error={!!getFieldError('title')}
                  helperText={getFieldError('title')}
                />

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', gap: 1, flex: 1, minWidth: 0 }}>
                    <TextField
                      name="deadlineDate"
                      label="Срок выполнения"
                      type="date"
                      value={formData.deadlineDate}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      inputProps={{ min: minDeadlineDate }}
                      slotProps={{ htmlInput: { min: minDeadlineDate } }}
                      error={!!getFieldError('deadlineDate')}
                      helperText={getFieldError('deadlineDate')}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        flex: 1.5,
                        minWidth: 0,
                        '& .MuiInputLabel-root': {
                          transform: 'translate(14px, -9px) scale(0.75)',
                          background: '#fff',
                          px: 0.5,
                          zIndex: 1,
                        },
                      }}
                    />
                    <TextField
                      name="deadlineTime"
                      label="Время"
                      type="time"
                      value={formData.deadlineTime}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      error={!!touched.deadlineDate && deadlineIsPast()}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        '& .MuiInputLabel-root': {
                          transform: 'translate(14px, -9px) scale(0.75)',
                          background: '#fff',
                          px: 0.5,
                          zIndex: 1,
                        },
                      }}
                    />
                  </Box>

                  <TextField
                    name="fixedPrice"
                    sx={{ flex: 1, minWidth: 0 }}
                    label="Стоимость"
                    type="number"
                    value={formData.fixedPrice}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    InputProps={{
                      startAdornment: <MonetizationOnIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />,
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* Tab 0: Клиент */}
            {activeTab === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    name="clientName"
                    label="Имя клиента"
                    value={formData.clientName}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="ФИО клиента"
                    required
                    error={!!getFieldError('clientName')}
                    helperText={getFieldError('clientName')}
                    InputProps={{
                      startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />,
                    }}
                  />

                  <TextField
                    fullWidth
                    name="phoneNumber"
                    label="Телефон"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    type="tel"
                    inputProps={{ inputMode: 'numeric' }}
                    InputProps={{
                      startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />,
                    }}
                  />
                </Box>

                <TextField
                  fullWidth
                  name="address"
                  label="Адрес"
                  value={formData.address}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  placeholder="Адрес выполнения заявки"
                  required
                  error={!!getFieldError('address')}
                  helperText={getFieldError('address')}
                  InputProps={{
                    startAdornment: <LocationOnIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />,
                  }}
                />

                <TextField
                  fullWidth
                  name="clientComment"
                  label="Комментарий клиента"
                  value={formData.clientComment}
                  onChange={handleInputChange}
                  placeholder="Дополнительная информация от клиента"
                  multiline
                  rows={3}
                />
              </Box>
            )}

            {/* Tab 2: Суть работы и вложения */}
            {activeTab === 2 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <TextField
                  fullWidth
                  name="description"
                  label="Суть работы"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Опишите подробно суть заявки"
                  multiline
                  rows={4}
                />

                <Box
                  {...getRootProps()}
                  sx={{
                    p: 2.5,
                    textAlign: 'center',
                    cursor: 'pointer',
                    border: '2px dashed',
                    borderRadius: 2,
                    borderColor: isDragActive ? 'primary.main' : 'divider',
                    bgcolor: isDragActive ? 'action.hover' : 'grey.50',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <input {...getInputProps()} />
                  <CloudUploadIcon sx={{ fontSize: 36, color: isDragActive ? 'primary.main' : 'text.disabled', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {isDragActive ? 'Отпустите файлы здесь' : 'Перетащите файлы или нажмите для выбора'}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.disabled' }}>
                    JPG, PNG, GIF, PDF, DOC, DOCX, TXT (макс. 20 МБ)
                  </Typography>
                </Box>

                {fileSizeWarning && (
                  <Alert
                    ref={fileSizeWarningRef}
                    severity="warning"
                    onClose={() => setFileSizeWarning('')}
                    sx={{ borderRadius: 2 }}
                  >
                    {fileSizeWarning}
                  </Alert>
                )}

                {files.length > 0 && (
                  <Stack spacing={0.75}>
                    {files.map((file, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                          <AttachFileIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                          <Tooltip title={file.name}>
                            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                              {file.name}
                            </Typography>
                          </Tooltip>
                          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                            {formatFileSize(file.size)}
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => removeFile(index)} sx={{ color: 'error.main' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button
          onClick={handleCancel}
          color="inherit"
          disabled={isSubmitting}
          sx={{ textTransform: 'none' }}
        >
          Отмена
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {activeTab > 0 && (
            <Button
              onClick={() => setActiveTab(prev => prev - 1)}
              disabled={isSubmitting}
              sx={{ textTransform: 'none' }}
            >
              Назад
            </Button>
          )}
          {activeTab < 2 ? (
            <Button
              variant="contained"
              onClick={() => setActiveTab(prev => prev + 1)}
              sx={{ textTransform: 'none' }}
            >
              Далее
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting || loading}
              startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Создать заявку
            </Button>
          )}
        </Box>
      </DialogActions>

      <Snackbar
        open={!!submitError}
        autoHideDuration={8000}
        onClose={() => setSubmitError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          onClose={() => setSubmitError(null)}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {submitError}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default CreateQuestForm;
